import FImageProcessor from '~/services/upload/image-processor';

const apiPaths = {
  createFile: '/users/:userId/uploads',                //post
  s3SignedUrl: '/users/:userId/uploads/presigned_url',  //get
  getDuplicates: '/users/:userId/forever_files/find_by_digest',
  s3SignedUrlCollaborative: '/users/:userId/collaborative_album_uploads/:albumId/presigned_url',
  createFileCollaborative: '/users/:userId/collaborative_album_uploads/:albumId'
};

export function useUploadApi() {
  return {
    async getS3SignedUrl(filename, type, collaborativeAlbum, shareToken) {
      try {
        const cacheBuster = generateUUID();
        const resource = collaborativeAlbum ? 's3SignedUrlCollaborative' : 's3SignedUrl';
        const payload = {
          type,
          filename,
          share_token: shareToken
        };
        const params = {
          route: {
            userId: (collaborativeAlbum && collaborativeAlbum.user.id) || useSessionStore().currentUserId,
            albumId: collaborativeAlbum && collaborativeAlbum.id
          },
          query: {
            cache_buster: cacheBuster
          }
        };
        const data = await useMarsApiV3().post(
          apiPaths[resource],
          {
            params,
            body: payload
            //{fSerializeResponses: false} //todo: see if we need this
          }
        );

        return {
          id: data.id,
          url: data.presigned_urls.original
        };
      } catch (e) {
        //todo: verify this is not needing to be changed to e.statusCode
        if (e.response && e.response.status === 413) {
          throw Error('STORAGE_FULL');
        }
        throw e;
      }
    },
    async getDuplicate(digest) {
      if (!digest) {
        return null;
      }

      try {
        const data = await useMarsApiV3().fetch(
          apiPaths.getDuplicates,
          {
            params: {
              route: {userId: useSessionStore().currentUserId},
              query: {digest}
            }
          }
        );

        return data.digests[0].id;
      } catch (e) {
        return null;
      }
    },
    async createFileRecord(file, {
      id,
      type,
      url,
      albumId,
      tagNames = [],
      personId,
      processorData = {},
      description,
      uploadSource,
      collaborativeAlbum,
      shareToken
    }) {
      const uploadSources = useUploadSources();

      const resource = collaborativeAlbum ? 'createFileCollaborative' : 'createFile';
      const payload = {
        id,
        type,
        album_id: albumId || null,
        tags: Array.from(tagNames),
        person_id: personId || null,
        file_name: file.name,
        file_size: file.size,
        file_url: url.split('?')[0],
        upload_source: uploadSource || uploadSources.defaultUploadSource,
        description,
        share_token: shareToken
      };


      if (type === 'video') {
        payload.taken_at = getFileLastModifiedDate(file);
      } else if (type === 'audio') {
        payload.taken_at = getFileLastModifiedDate(file);
        payload.digest = processorData.sha1;
      } else {
        Object.assign(payload, {
          digest: processorData.sha1,
          width: processorData.width,
          height: processorData.height,

          //check that orientation exists and is in range (1-8), otherwise fall back to default
          orientation: processorData.orientation && numberInRange(processorData.orientation, 1, 8)
            ? processorData.orientation
            : DEFAULT_ORIENTATION,
          taken_at: processorData.takenAt || getFileLastModifiedDate(file)
        });

        //if the original orientation matches the array, flip width and height.
        /*commenting this code out. I believe this was added in the original build of F2 when we were using an exif parser
        on the client. EXIF parser output dimensions were not are with respect to the exif orientation, not the display.
        At a later date, we then switched our image processor to process the image on the client using the image tag, which
        reports exif adjusted dims. And thus, a bug live on for years undetected until our list grids respected the aspect display
         */
        /*const revOrient = [5, 6, 7, 8];
        if (revOrient.includes(payload.orientation)) {
          payload.width = processorData.height;
          payload.height = processorData.width;
        }*/
      }

      const params = {
        route: {
          userId: (collaborativeAlbum && collaborativeAlbum.user.id) || useSessionStore().currentUserId,
          albumId: collaborativeAlbum && collaborativeAlbum.id
        }
      };

      try {
        const data = await useMarsApiV3().post(
          apiPaths[resource],
          {
            params,
            body: payload
            // {fSerializeResponses: false}
          }
        );

        return data.photo;
      } catch (e) {
        //todo: verify this is not needing to be changed to e.statusCode
        if (e.response && e.response.status === 413) {
          throw Error('STORAGE_FULL');
        }
        throw e;
      }
    },

    async uploadImage(imageData, {
      fileName,
      takenAt,
      description,
      albumId,
      tagNames,
      uploadSource,
      foreverFileType = 'photo'
    } = {}) {

      const fileBlob = imageData instanceof Blob
        ? imageData
        : (() => {
          const binaryStr = atob(imageData.split(',')[1]);
          const bytes = [];

          for (let i = 0; i < binaryStr.length; i++) {
            bytes.push(binaryStr.charCodeAt(i));
          }

          return new Blob([new Uint8Array(bytes)], {type: 'image/jpeg'});
        })();

      const desiredName = fileName || (imageData instanceof File ? imageData.name : 'image.jpg');
      const uploadFile = (imageData instanceof File && (!fileName || imageData.name === fileName))
        ? imageData
        : new File([fileBlob], desiredName, {type: fileBlob.type || 'image/jpeg'});

      const {id, url} = await this.getS3SignedUrl(fileName, foreverFileType);
      const [parsedImageData] = await Promise.all([
        new FImageProcessor(uploadFile, {skipValidation: true}).process(),
        $fetch(url, {
          method: 'PUT',
          body: uploadFile
        })
      ]);

      return this.createFileRecord(parsedImageData.file, {
        id,
        url,
        type: foreverFileType,
        albumId,
        tagNames,
        uploadSource,
        processorData: {
          ...parsedImageData,
          takenAt   //note: overrides the value in parsedImageData
        },
        description
      });
    }
  };
}
